import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { getAccessToken } from '../auth/tokenStorage';
import telemetry from './telemetry.service';

// Create browser history for route tracking
const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();

// Initialize Application Insights
const initializeAppInsights = () => {
  const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;
  const shouldLog = process.env.REACT_APP_ENABLE_LOGGING === 'true' && process.env.REACT_APP_ENVIRONMENT === 'development';

  if (shouldLog) {
    console.log('AppInsights: Initializing with environment:', process.env.REACT_APP_ENVIRONMENT);
    console.log('AppInsights: Connection string present:', !!connectionString && connectionString !== 'null');
  }
   
  // Only initialize if connection string is provided and not 'null'
  if (!connectionString || connectionString === 'null' || connectionString === 'undefined') {
    if (shouldLog) {
      console.log('Application Insights: No valid connection string provided, telemetry disabled');
    }
    return null;
  }

  const appInsights = new ApplicationInsights({
    config: {
      connectionString: connectionString,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { history: browserHistory }
      },
      enableAutoRouteTracking: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      enableAjaxPerfTracking: true,
      enableAjaxErrorStatusText: true,
      enableUnhandledPromiseRejectionTracking: true,
      autoTrackPageVisitTime: true,
      disableFetchTracking: false,
      
      // Performance settings
      maxBatchSizeInBytes: 10000,
      maxBatchInterval: 15000,
      
      // User tracking
      enableSessionStorageBuffer: true,
      
      // Enable telemetry for all environment
      disableTelemetry: false,
      
      // Custom properties to be added to all telemetry
      accountId: process.env.REACT_APP_ENVIRONMENT || 'unknown',
    }
  });

  appInsights.loadAppInsights();
  
  // Set authenticated user context if available
  const token = getAccessToken();
  if (token) {
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      appInsights.setAuthenticatedUserContext(
        tokenPayload.email || tokenPayload.username,
        tokenPayload.org_id,
        true
      );
    } catch (e) {
      console.error('Failed to set user context', e);
    }
  }

  // Add custom properties to all telemetry
  appInsights.addTelemetryInitializer((envelope) => {
    envelope.data = envelope.data || {};
    envelope.tags = envelope.tags || {};
    envelope.data['app.version'] = process.env.REACT_APP_VERSION || '1.0.0';
    envelope.data['app.environment'] = process.env.REACT_APP_ENVIRONMENT || 'unknown';
    return true;
  });

  return appInsights;
};

// Initialize on import
const appInsights = initializeAppInsights();

const FEATURE_MAP = {
  'survey-insights': 'survey_insights',
  'sitecollections': 'site_collections',
  'contents': 'contents',
  'flows': 'flows',
  'visibility': 'visibility',
  'apikey': 'api_keys',
  'apiKeys': 'api_keys',
  'organization': 'organizations',
  'users': 'users',
  'pending': 'pending_users',
  'dashboard': 'dashboard',
  'metering': 'metering',
  'analytics-iap': 'iap_usage_analytics',
  'analytics-iap-user-journey': 'iap_user_journey',
  'analytics-iap-unique-users': 'iap_unique_users',
  'analytics-iap-time-spent': 'iap_time_spent',
  'analytics-iap-flows-usage': 'iap_flows_usage',
  'analytics-iap-flow-dropoff': 'iap_flow_dropoff',
  'analytics-iap-browser': 'iap_browser_distribution',
  'analytics-iap-device': 'iap_device_distribution',
  'analytics-iap-geography': 'iap_geography'
};

// Export telemetry methods
export const trackEvent = (name, properties = {}, measurements = {}) => {
  setTimeout(() => {
    if (appInsights) {
      appInsights.trackEvent({ name }, properties, measurements);
    }

    try {
      if (properties.skipTelemetryTrack || properties.featureKey === null) {
        return;
      }

      let featureKey = properties.featureKey;
      const siteCollectionId = properties.siteCollectionId || null;

      if (!featureKey && properties?.section) {
        const mappedFeature = FEATURE_MAP[properties.section];
        featureKey = mappedFeature || properties.section;
      }

      if (featureKey) {
        telemetry.track('feature.used', {
          featureKey,
          siteCollectionId,
          organizationId: properties.organizationId || properties.orgId || null,
          classification: 'Informational',
          dimensions: properties
        });
      }
    } catch (err) {
      console.error('Failed to forward event to Telemetry SDK', err);
    }
  }, 0);
};

export const trackException = (error, properties = {}) => {
  if (appInsights) {
    appInsights.trackException({ 
      error, 
      severityLevel: 2 // Error 
    }, properties);
  }
};

export const trackPageView = (name, url, properties = {}) => {
  if (appInsights) {
    appInsights.trackPageView({ name, uri: url }, properties);
  }
};

export const trackMetric = (name, average, properties = {}) => {
  if (appInsights) {
    appInsights.trackMetric({ name, average }, properties);
  }
};

export const setAuthenticatedUser = (userId, accountId = null) => {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }
};

export const clearAuthenticatedUser = () => {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
};

export const startTrackEvent = (name) => {
  if (appInsights) {
    appInsights.startTrackEvent(name);
  }
};

export const stopTrackEvent = (name, properties = {}, measurements = {}) => {
  if (appInsights) {
    appInsights.stopTrackEvent(name, properties, measurements);
  }
};

export const flush = () => {
  if (appInsights) {
    appInsights.flush();
  }
};

export { reactPlugin, browserHistory };
export default appInsights;